import React, { useState, useEffect } from "react";
import LabGridRow from "./LabGridRow";
import { ILabGridProps } from "../../typings/models/LabGridProps";

const LabGrid: React.FC<ILabGridProps> = ({
  data,
  isLoading,
  setData,
  headerColumns,
  sno,
  allData,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const itemsPerPage = 10;
  const maxPagesToShow = 5;

  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };
  const getDefaultExpandedRows = () => {
    const expandedState: { [key: string]: boolean } = {};
    allData.forEach((row: any) => {
      if (
        allData.some(
          (subRow) =>
            subRow.orignalLabApplicationNumber === row.applicationNumber
        )
      ) {
        expandedState[row.applicationNumber] = true;
      }
    });
    return expandedState;
  };

  const filteredData = data.filter((item: any) =>
    Object.values(item).some((value) =>
      value?.toString().toLowerCase().includes(searchQuery?.toLowerCase())
    )
  );
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>(
    getDefaultExpandedRows
  );

  const sortedData = filteredData.sort((a: any, b: any) => {
    if (sortColumn === null) return 0;

    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    } else if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    } else {
      return 0;
    }
  });

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const renderPageButtons = () => {
    const pageButtons = [];
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxPagesToShow) {
      const halfMaxPages = Math.floor(maxPagesToShow / 2);
      startPage = Math.max(currentPage - halfMaxPages, 1);
      endPage = startPage + maxPagesToShow - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = endPage - maxPagesToShow + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePagination(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (totalPages > maxPagesToShow && endPage < totalPages) {
      pageButtons.push(
        <li key="separator" className="page-item disabled">
          <button className="page-link">...</button>
        </li>
      );
      pageButtons.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePagination(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pageButtons;
  };

  const mainRows = paginatedData.filter(
    (row: any) => !row.orignalLabApplicationNumber
  );

  const subRowsMap = paginatedData.reduce((acc: any, row: any) => {
    if (row.orignalLabApplicationNumber) {
      if (!acc[row.orignalLabApplicationNumber]) {
        acc[row.orignalLabApplicationNumber] = [];
      }
      acc[row.orignalLabApplicationNumber].push(row);
    }
    return acc;
  }, {});

  return (
    <div>
      <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={handleSearch}
        className="form-control mb-3 search-input"
      />

      <table className="table table-bordered table-hover">
        <thead>
          <tr>
            {headerColumns.map((column, index) => (
              <th
                key={index}
                className="grid-header"
                onClick={() => handleSort(column.sortKey)}
              >
                {column.columnName}
                {sortColumn === column.sortKey && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((data: any, index: number) => {
            const subRows = subRowsMap[data.applicationNumber] || [];

            const isExpanded = expandedRows[data.applicationNumber];
            const showAmendmentButton = subRows.every(
              (subRow: any) => subRow.showAmendButton === 1
            );

            const showCancelButton = subRows.every(
              (subRow: any) => subRow.applicationStatus === 1
            );
            const showDuplicateButton = subRows.every(
              (subRow: any) => subRow.applicationStatus === 1
            );

            return (
              <LabGridRow
                key={data.applicationNumber}
                data={data}
                subRows={subRows}
                isExpanded={isExpanded}
                showAmendmentButton={showAmendmentButton}
                showDuplicateButton={showDuplicateButton}
                showCancelButton={showCancelButton}
                sno={startIndex + index + 1}
              />
            );
          })}
        </tbody>
      </table>

      <nav aria-label="Page navigation">
        <ul className="pagination justify-content-center">
          {currentPage > 1 && (
            <li className="page-item">
              <button
                className="page-link"
                onClick={() => handlePagination(currentPage - 1)}
              >
                Previous
              </button>
            </li>
          )}
          {renderPageButtons()}
          {currentPage < totalPages && (
            <li className="page-item">
              <button
                className="page-link"
                onClick={() => handlePagination(currentPage + 1)}
              >
                Next
              </button>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
};

export default LabGrid;
